import React, { useState, useEffect, createContext, Component, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { TabContent, Nav, NavLink, NavItem, TabPane } from 'reactstrap';
import TabsWrapper from './pure/Tabs';

const propTypes = {
  tabs: PropTypes.array,
  renderOnlyActive: PropTypes.bool,
};

/**
 * @type {useTabs}
 */
const initial = () => null
export default class Tabs extends Component {
  static Context = createContext(initial())

  static useContext = () => useContext(Tabs.Context)

  static useTab = tab => {
    const { tabs, update } = Tabs.useContext()
    const [enabled, setEnabled] = useState(true)

    useEffect(() => {
      tabs.add(tab)
      update()
    }, [])

    useEffect(() => {
      tabs.enable(tab, enabled)
      update()
    }, [enabled])

    return { enabled, setEnabled }
  }

  static Tab = ({ tab: [tabId, label], children, ...props }) => {
    Tabs.useTab([tabId, label])

    const { active, renderOnlyActive } = Tabs.useContext()

    if (active !== tabId && renderOnlyActive) return null

    return <TabPane tabId={tabId} {...props}>
      {children}
    </TabPane>
  }

  NavItem = ({ tab: [id, label, hidden = false] }) => {
    if (hidden)
      return null

    const { active, setActive, tabs } = Tabs.useContext()

    return <NavItem key={id} className={classnames({ active: active === id })}
      onClick={() => setActive(id)}>
      <NavLink>{label}</NavLink>
    </NavItem>
  }

  TabContent = () => {
    const { children, scroll, contentClassName } = this.props
    const { active } = Tabs.useContext()

    return <TabContent activeTab={active} className={`${contentClassName} ${scroll ? 'tab-content-scrollable' : ''}`}>
      {children}
    </TabContent>
  }

  Content = () => {
    const { tabs: tabsDefinition, hook, renderOnlyActive } = this.props
    const tabs = hook || useTabs(tabsDefinition, renderOnlyActive)

    return <TabsWrapper>
      <Tabs.Context.Provider value={tabs}>
        <Nav tabs>
          {tabs.tabs.map((tab, idx) => <this.NavItem key={idx} tab={tab} />)}
        </Nav>
        <this.TabContent />
      </Tabs.Context.Provider>
    </TabsWrapper>
  }

  render() {
    return <this.Content />
  }
}

Tabs.propTypes = propTypes
Tabs.defaultProps = {
  tabs: [],
}

export function useTabs(defaultTabs, renderOnlyActive = false) {
  const [active, setActive] = useState('')
  const tabs = useMemo(() => new TabArray(defaultTabs), [defaultTabs])
  const [tabCount, setTabCount] = useState(defaultTabs.length)

  const update = () => { setTabCount(tabs.length) }
  useEffect(update, [tabs.length])

  useEffect(() => {
    const [firstTab] = tabs || []
    const [firstTabId] = firstTab || []
    firstTabId && setActive(firstTabId)
  }, [tabs[0] && tabs[0][0]])

  return {
    renderOnlyActive,
    active,
    setActive,
    setActiveOrder: order => setActive(tabs[order - 1][0]),
    tabs, update,
    isActive: tabId => tabId === active
  }
}

class TabArray extends Array {
  constructor(array) {
    super()
    this.tabs = {}
    if (Array.isArray(array))
      for (let item of array) {
        this.add(item)
      }
  }

  add = ([tabId, label]) => {
    const tab = [tabId, label]

    const order = this.length
    this.push(tab)

    return this.tabs[tabId] = { order, tab }
  }

  enable = ([tabId], toggle) => {
    const { order, tab } = this.tabs[tabId]

    if (toggle) {
      if (!this.includes(tab)) {
        this.splice(order, 0, tab)
      }
    } else {
      if (this.includes(tab)) {
        this.splice(this.indexOf(tab), 1)
      }
    }
  }
}